import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Form, Input, Button, DatePicker, Divider, message } from 'antd';
import { ILicenseData, FormMultipleLicense } from 'types/newUniprofile';
import StateSelect from 'components/form/StateSelect';
import FileUpload from 'components/form/FileUpload';
import LicenseImg from 'components/LicenseImg';
import { providerLicenseBatchUpdate } from 'api/provider';
import s from './s.module.less';
import dayjs from 'dayjs';
import { formatDate } from 'utils/common';
import FileUploadPrivate from 'components/form/FileUploadPrivate';

interface Props {
    initValue?: ILicenseData[];
    isEditing?: boolean;
    refetch?: () => void;
}

const PracticeLicense = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, refetch } = props;
    const [form] = Form.useForm<FormMultipleLicense>();

    useEffect(() => {
        if (initValue?.length) {
            form.setFieldsValue({
                licenses: initValue.map((item) => ({
                    ...item,
                    expireDate: item.expireDate ? dayjs(item.expireDate) : undefined,
                })),
            });
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const params = await form.validateFields();
            const licenseArrary: ILicenseData[] = (params.licenses || []).map((license) => ({
                ...license,
                expireDate: formatDate(license.expireDate),
                licenseType: '',
            }));
            const result = await providerLicenseBatchUpdate(licenseArrary);
            if (result.error) {
                message.error(result.error);
            } else {
                message.success('License information has been updated');
                await refetch?.();
            }
        } catch (e) {
            console.error(e);
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => form.validateFields(),
    }));

    const renderEditing = () => {
        return (
            <Form
                form={form}
                autoComplete="off"
                layout="vertical"
            >
                <Form.List name="licenses" initialValue={[{}]}>
                    {(fields, { add, remove }) => {
                        const licensesLength = fields.length;
                        return (
                            <div id="practiceLicense">
                                {fields.map((field, inx) => {
                                    return (
                                        <Form.Item
                                            noStyle
                                            key={field.key}
                                        >
                                            <div>
                                                <div className={s.subModule}>
                                                    <h3>{`License ${inx + 1}`}</h3>
                                                    <Divider style={{ margin: '0 0 12px' }} />
                                                    <Form.Item name="id" style={{ position: 'absolute' }}>
                                                        <Input type="hidden" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        style={{ width: '320px' }}
                                                        label="State for licenses"
                                                        name={[field.name, 'state']}
                                                        validateFirst
                                                        rules={[
                                                            { required: true, message: 'State is required' },
                                                        ]}
                                                    >
                                                        <StateSelect />
                                                    </Form.Item>
                                                    <div className="flex gap-4">

                                                        <Form.Item
                                                            style={{ width: '320px' }}
                                                            label="License number"
                                                            name={[field.name, 'licenseNumber']}
                                                            validateFirst
                                                            rules={[
                                                                { required: true, message: 'License number is required' },
                                                            ]}
                                                        >
                                                            <Input maxLength={25} placeholder="What is the license number?  " />
                                                        </Form.Item>

                                                        <Form.Item
                                                            style={{ width: '320px' }}
                                                            label="Expiration date"
                                                            name={[field.name, 'expireDate']}
                                                            rules={[{ required: true, message: 'Expiration date is required' }]}
                                                        >
                                                            <DatePicker
                                                                disabledDate={(date) => {
                                                                    const now = (new Date()).getTime();
                                                                    if (!date) {
                                                                        return false;
                                                                    }
                                                                    return date?.valueOf() < now;
                                                                }}
                                                                style={{ width: '100%' }}
                                                            />
                                                        </Form.Item>

                                                    </div>
                                                    <Form.Item
                                                        style={{ width: '320px' }}
                                                        label="License name"
                                                        name={[field.name, 'type']}
                                                        rules={[{ required: true, message: 'License name is required' }]}
                                                    >
                                                        <Input placeholder="Ex, Nurse practitioner," />
                                                    </Form.Item>

                                                    <Form.Item
                                                        style={{ width: '300px' }}
                                                        label="Upload the copy of the license"
                                                        name={[field.name, 'url']}
                                                        rules={[{ required: true, message: 'License copy is required' }]}
                                                    >
                                                        <FileUploadPrivate
                                                            uploadType="LICENSE"
                                                        />
                                                        {/* <FileUpload /> */}
                                                    </Form.Item>

                                                    {field.name !== 0 &&
                                                        <div className={s.removeWrap}>
                                                            <Button
                                                                onClick={() => remove(field.name)}
                                                                danger
                                                                size="small"
                                                            >Remove
                                                            </Button>
                                                        </div>}
                                                </div>
                                                {field.name === licensesLength - 1 &&
                                                    <div
                                                        onClick={() => {
                                                            add();
                                                            const wrap = document.getElementById('practiceLicense');
                                                            setTimeout(() => {
                                                                wrap?.parentElement?.parentElement?.scroll({
                                                                    top: 1000000,
                                                                    behavior: 'smooth',
                                                                });
                                                            }, 200);
                                                        }}
                                                        className={s.add}
                                                    >
                                                        + Add another practice license
                                                    </div>
                                                }
                                            </div>
                                        </Form.Item>);
                                })}
                            </div>);
                    }}
                </Form.List>
            </Form>
        );
    };

    const renderReview = () => {
        const licensesArray: ILicenseData[] = initValue?.filter((item: ILicenseData) => !!(item && item.licenseNumber)) || [];

        if (!licensesArray?.length) {
            return (
                <div>
                    Not provided
                </div>
            );
        }
        return (
            <div>
                {
                    licensesArray?.map((item, inx) => (
                        <div
                            className={s.subModule}
                            key={`${item.licenseNumber}_${inx}`}
                        >
                            <div className={s.row}>
                                <div
                                    style={{
                                        width: '300px',
                                    }}
                                    className={s.displayItem}
                                >
                                    <div className={s.label}>License state</div>
                                    <div className={s.value}>{item.state}</div>
                                </div>
                            </div>
                            <div className={s.row}>
                                <div
                                    style={{
                                        width: '300px',
                                    }}
                                    className={s.displayItem}
                                >
                                    <div className={s.label}>Practice license number</div>
                                    <div className={s.value}>{item.licenseNumber}</div>
                                </div>
                                <div className={s.displayItem}>
                                    <div className={s.label}>Expiration date</div>
                                    <div className={s.value}>{item.expireDate ? dayjs(item.expireDate).format('MM-DD-YYYY') : ''}</div>
                                </div>
                            </div>
                            <div className={s.row}>
                                <div
                                    style={{
                                        width: '300px',
                                    }}
                                    className={s.displayItem}
                                >
                                    <div className={s.label}>License name</div>
                                    <div className={s.value}>{item.type}</div>
                                </div>
                            </div>
                            <div className={s.row}>
                                <div
                                    style={{
                                        width: '400px',
                                    }}
                                    className={s.displayItem}
                                >
                                    <div className={s.label}>Uploaded license</div>
                                    <div className={s.value}>
                                        <LicenseImg src={item.fullUrl || item.url} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                }
            </div>
        );
    };

    return (
        <div className={s.wrap}>
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default PracticeLicense;
